import { ReactElement } from 'react'

import { Avatar, Badge, Button, List } from 'antd'
import orderBy from 'lodash/orderBy'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { updateTriggeredAlert } from 'api/Cerebro/orgScoped/alertsApi'
import { ArrowRightButton } from 'components/Buttons'
import { Drawer, Flex, Icon, Text } from 'components/core'
import { AlertLink } from 'components/Links'
import { date } from 'configuration/dayjs'
import { useCerebroApiRequest } from 'hooks'
import { getPath } from 'routing/pagePaths'
import { alertUrl } from 'routing/urls'
import { TriggeredAlert } from 'types'

import * as styles from './styles.scss'

interface AlertsDrawerProps {
    triggeredAlerts: TriggeredAlert[]
    visible: boolean
    onClose: () => void
}

export const AlertsDrawer = ({
    triggeredAlerts,
    visible,
    onClose,
}: AlertsDrawerProps): ReactElement => {
    const { t } = useTranslation(['common', 'navigation'])
    const navigate = useNavigate()
    const makeCerebroApiRequest = useCerebroApiRequest()

    const handleDismissTriggeredAlert = async (
        triggeredAlert: TriggeredAlert
    ): Promise<void> => {
        await makeCerebroApiRequest({
            request: updateTriggeredAlert(triggeredAlert.id, {
                status: 'read',
            }),
            onRequestSuccess: () => {
                navigate(alertUrl(triggeredAlert.alert_configuration))
            },
        })
    }

    return (
        <Drawer
            title={
                <Flex direction="row" alignItems="center" gap={8}>
                    <Text variant="headingSm">
                        {t('common:triggeredAlerts', 'Triggered Alerts')}
                    </Text>
                    <Link
                        to={`${getPath('settingsPage')}/alerts/?tab=triggeredAlerts`}
                        style={{ fontSize: 14 }}
                    >
                        {t('common:viewAll', 'View All')}
                    </Link>
                </Flex>
            }
            onClose={onClose}
            open={visible}
            width={460}
            rootClassName={styles.drawer}
        >
            <List
                dataSource={orderBy(
                    triggeredAlerts,
                    ['triggered_at'],
                    ['desc']
                )}
                locale={{
                    emptyText: t(
                        'navigation:TopNav.AlertsDrawer.emptyText',
                        'No new alerts.'
                    ),
                }}
                footer={
                    <Flex justifyContent="center" alignItems="normal">
                        <Button
                            type="primary"
                            onClick={() => {
                                navigate(getPath('alertCreationPage'))
                            }}
                            icon={<Icon name="PLUS" size={13} />}
                        >
                            {t(
                                'navigation:TopNav.AlertsDrawer.createAlertBtnText',
                                'Create New Alert'
                            )}
                        </Button>
                    </Flex>
                }
                renderItem={(alert) => (
                    <List.Item
                        actions={[
                            <ArrowRightButton
                                key="dismiss-alert"
                                onClick={() =>
                                    handleDismissTriggeredAlert(alert)
                                }
                            />,
                        ]}
                    >
                        <List.Item.Meta
                            avatar={
                                <Badge dot={alert.status === 'unread'}>
                                    <Avatar
                                        style={{
                                            backgroundColor: 'transparent',
                                        }}
                                    >
                                        <Icon name="BELL" color="textMuted" />
                                    </Avatar>
                                </Badge>
                            }
                            title={<AlertLink triggeredAlert={alert} />}
                            className={styles['drawer-list-item']}
                            description={
                                <p>
                                    <Trans i18nKey="navigation:TopNav.AlertsDrawer.triggeredOn">
                                        <strong>Triggered On:</strong>{' '}
                                        {{
                                            triggeredOn: date(
                                                alert.triggered_at
                                            ).format('LLL'),
                                        }}
                                    </Trans>
                                </p>
                            }
                        />
                    </List.Item>
                )}
            />
        </Drawer>
    )
}
